/**
 * pp-filter-tracking — Sprint 2 del facetado en collection.detailed.
 *
 * 1. Scroll preservation
 *    Cuando el cliente cambia un filtro, Shopify recarga la página y
 *    el scroll vuelve al top. Frustrante porque el cliente estaba
 *    leyendo el grid en mitad de la página. Fix: guardar scrollY en
 *    sessionStorage justo antes del navigation y restaurarlo al
 *    cargar la nueva página. Auto-clean del storage después de
 *    restore para no afectar navegación posterior a otras páginas.
 *
 * 2. Tracking GA4 (insight CTO)
 *    Emitir eventos cuando el cliente aplica/quita filtros para que
 *    el equipo pueda decidir stock y SEO basado en datos reales:
 *      - filter_applied   (filter checkbox checked)
 *      - filter_removed   (chip × clicked o checkbox unchecked)
 *      - filters_cleared  (link 'Limpiar todos' clicked)
 *      - sort_changed     (sort dropdown changed)
 *    Si window.gtag no existe (sin GA4 instalado), el evento se hace
 *    no-op — no rompe nada.
 *
 * Convenciones:
 *   - Vanilla JS, sin deps.
 *   - Self-guard via data-attribute en document para evitar doble
 *     bind si el script se carga 2 veces.
 *   - Event delegation desde document — sobrevive a section reloads
 *     del customizer Shopify.
 */
(function () {
  'use strict';

  if (document.documentElement.dataset.ppFilterTrackingBound === '1') return;
  document.documentElement.dataset.ppFilterTrackingBound = '1';

  var STORAGE_KEY = 'pp_filter_scroll_y';
  var COLLECTION_HANDLE_KEY = 'pp_filter_scroll_handle';

  // ── 1. Scroll preservation ────────────────────────────────────

  function saveScroll() {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(window.scrollY));
      // Guardamos también el handle para solo restaurar si seguimos
      // en la misma collection (evita scroll raro al navegar a otra
      // collection desde un link).
      var wrap = document.querySelector('[data-pp-collection-handle]');
      if (wrap) {
        sessionStorage.setItem(
          COLLECTION_HANDLE_KEY,
          wrap.getAttribute('data-pp-collection-handle') || ''
        );
      }
    } catch (e) {
      // sessionStorage puede fallar en private mode — failure tolerant
    }
  }

  function restoreScroll() {
    var y, savedHandle, currentHandle;
    try {
      y = sessionStorage.getItem(STORAGE_KEY);
      savedHandle = sessionStorage.getItem(COLLECTION_HANDLE_KEY);
    } catch (e) {
      return;
    }
    if (!y) return;

    var wrap = document.querySelector('[data-pp-collection-handle]');
    currentHandle = wrap ? wrap.getAttribute('data-pp-collection-handle') : null;
    if (savedHandle && currentHandle && savedHandle !== currentHandle) {
      // Cambió de collection — no restaurar.
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(COLLECTION_HANDLE_KEY);
      } catch (e) {}
      return;
    }

    // Restore en el próximo frame para que el layout esté pintado.
    var yNum = parseInt(y, 10);
    if (isNaN(yNum)) return;
    requestAnimationFrame(function () {
      window.scrollTo(0, yNum);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(COLLECTION_HANDLE_KEY);
      } catch (e) {}
    });
  }

  // ── 2. GA4 tracking helper ────────────────────────────────────

  function track(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    try {
      var enriched = Object.assign(
        {
          collection_handle: getCollectionHandle(),
          collection_total: getCollectionTotal(),
        },
        params || {}
      );
      window.gtag('event', eventName, enriched);
    } catch (e) {
      // GA4 puede crashear con datos raros; failure tolerant.
    }
  }

  function getCollectionHandle() {
    var wrap = document.querySelector('[data-pp-collection-handle]');
    return wrap ? wrap.getAttribute('data-pp-collection-handle') : '';
  }

  function getCollectionTotal() {
    var wrap = document.querySelector('[data-pp-collection-total]');
    return wrap ? parseInt(wrap.getAttribute('data-pp-collection-total') || '0', 10) : 0;
  }

  // ── 3. Event listeners ────────────────────────────────────────

  // Filter sidebar checkbox change.
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.tagName !== 'INPUT') return;
    if (input.type !== 'checkbox') return;
    var label = input.closest('.pp-col-detail__filter-label');
    if (!label) return;

    saveScroll();

    var summary = input.closest('details');
    var filterLabel = summary ? (summary.querySelector('summary') || {}).textContent : '';
    var valueLabel = (label.querySelector('.pp-col-detail__filter-value') || {}).textContent;
    // Filtro Sí/No (una sola casilla, sin <details>): el nombre viaja en
    // data-pp-filter-label y el valor es siempre el «Sí».
    if (label.hasAttribute('data-pp-filter-label')) {
      filterLabel = label.getAttribute('data-pp-filter-label');
      valueLabel = 'Sí';
    }

    track(input.checked ? 'filter_applied' : 'filter_removed', {
      filter_label: (filterLabel || '').trim().split('\n')[0],
      filter_value: (valueLabel || '').trim(),
      source: 'sidebar_checkbox',
    });
  });

  // Sort dropdown change.
  document.addEventListener('change', function (e) {
    if (!e.target.matches('[data-pp-sort]')) return;
    saveScroll();
    track('sort_changed', {
      sort_value: e.target.value,
      source: 'sort_dropdown',
    });
  });

  // Active filters chip click (× para remover individual).
  document.addEventListener('click', function (e) {
    var chip = e.target.closest('.pp-col-detail__active-filters-chip');
    if (!chip) return;
    saveScroll();
    var label = (chip.querySelector('.pp-col-detail__active-filters-chip-label') || {}).textContent;
    var value = (chip.querySelector('.pp-col-detail__active-filters-chip-value') || {}).textContent;
    // Filtro Sí/No: la etiqueta visible es sólo «Con tu logo»; los datos van aparte.
    if (chip.hasAttribute('data-pp-filter-label')) {
      label = chip.getAttribute('data-pp-filter-label');
      value = chip.getAttribute('data-pp-filter-value');
    }
    track('filter_removed', {
      filter_label: (label || '').replace(':', '').trim(),
      filter_value: (value || '').trim(),
      source: 'active_chip',
    });
  });

  // "Limpiar todos" click.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.pp-col-detail__active-filters-clear');
    if (!link) return;
    saveScroll();
    track('filters_cleared', {
      source: 'clear_all_link',
    });
  });

  // Pagination — preserve scroll para no perder la posición del cliente
  // que acaba de pasar a la página 2 y quiere volver atrás al mismo spot.
  document.addEventListener('click', function (e) {
    var pag = e.target.closest('.pp-col-detail__pagination-link, .pp-col-detail__pagination-page');
    if (!pag) return;
    if (pag.tagName !== 'A') return;
    saveScroll();
  });

  // ── 4. Fallback del estado vacío cuando Shopify pierde el contador ──
  //
  // MEDIDO EN VIVO 24-sep-2026 (ver sections/pp-collection-detailed.liquid):
  // si la combinación de filtros da CERO resultados, Shopify deja de
  // reportar `collection.filters.active_values`, así que en Liquid
  // `active_filter_count` cae a 0 y la sección pinta la rama "genérica"
  // del vacío — la misma que ve quien nunca filtró nada. La rama rica
  // (título + hint + "Limpiar filtros" + Paco) nunca se ofrecía para el
  // caso que la motivó.
  //
  // Liquid no puede leer el query string del lado del servidor, así que la
  // única fuente confiable de "¿el cliente SÍ filtró?" es el propio
  // location.search del navegador — Shopify nunca deja de ponerlo ahí,
  // aunque deje de contarlo server-side. Si aparece, revelamos el fallback
  // (ya venía en el HTML, oculto) y escondemos el genérico.
  function revealFilteredEmptyState() {
    var fallback = document.querySelector('[data-pp-empty-filtered-fallback]');
    if (!fallback) return;
    if (!/[?&]filter\./.test(window.location.search)) return;
    fallback.hidden = false;
    document.querySelectorAll('[data-pp-empty-generic]').forEach(function (el) {
      el.hidden = true;
    });
  }

  // ── 5. Restore on load ────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      restoreScroll();
      revealFilteredEmptyState();
    });
  } else {
    restoreScroll();
    revealFilteredEmptyState();
  }
})();
